import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getKanbanData, saveKanbanData } from '../services/api';
import LoadingOverlay from '../components/LoadingOverlay';
import './Kanban.css';

interface KanbanComment {
  id: string;
  text: string;
  author: string;
  timestamp: string;
}

interface KanbanTask {
  id: string;
  title: string;
  description: string;
  status: 'todo' | 'in-progress' | 'completed';
  priority: 'low' | 'medium' | 'high' | 'critical';
  assignee?: string;
  createdBy?: string;
  duration?: string;
  dueDate?: string;
  page: string;
  boardId: string;
  comments: KanbanComment[];
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}

interface KanbanColumn {
  id: string;
  title: string;
  tasks: KanbanTask[];
}

interface KanbanBoard {
  id: string;
  name: string;
  description: string;
  columns: KanbanColumn[];
}

const Kanban: React.FC = () => {
  const { user } = useAuth();
  const [boards, setBoards] = useState<KanbanBoard[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);



  // Modal states
  const [showAddBoard, setShowAddBoard] = useState(false);
  const [newBoardName, setNewBoardName] = useState('');
  const [newBoardDescription, setNewBoardDescription] = useState('');
  
  // Task editing states
  const [editingTask, setEditingTask] = useState<KanbanTask | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<KanbanTask | null>(null);
  const [commentInputs, setCommentInputs] = useState<{ [taskId: string]: string }>({});

  // Create new task states
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [newTask, setNewTask] = useState<Partial<KanbanTask>>({
    title: '',
    description: '',
    priority: 'medium',
    page: 'Overview',
    assignee: '',
    createdBy: '',
    duration: '',
    dueDate: '',
    status: 'todo'
  });

  // Filter states
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [pageFilter, setPageFilter] = useState<string>('all');



  // Load data from API
  useEffect(() => {
    const loadKanbanData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getKanbanData();
        if (response.status === 'success') {
          setBoards(response.data.boards);
        }
      } catch (err) {
        console.error('Error loading Kanban data:', err);
        setError('Failed to load Kanban data');
      } finally {
        setLoading(false);
      }
    };

    loadKanbanData();
  }, []);

  // Save data to API
  const saveData = async (updatedBoards: KanbanBoard[]) => {
    try {
      setSaving(true);
      await saveKanbanData(updatedBoards);
    } catch (err) {
      console.error('Error saving Kanban data:', err);
      setError('Failed to save Kanban data');
    } finally {
      setSaving(false);
    }
  };

  const [draggedTaskId, setDraggedTaskId] = useState<string | null>(null);
  const [dragOverColumnId, setDragOverColumnId] = useState<string | null>(null);

  const handleDragStart = (e: React.DragEvent, taskId: string) => {
    e.dataTransfer.setData('text/plain', taskId);
    setDraggedTaskId(taskId);
    e.currentTarget.classList.add('dragging');
  };

  const handleDragEnd = (e: React.DragEvent) => {
    setDraggedTaskId(null);
    setDragOverColumnId(null);
    e.currentTarget.classList.remove('dragging');
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleColumnDragOver = (e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    setDragOverColumnId(columnId);
  };

  const handleColumnDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverColumnId(null);
  };

  const handleDrop = async (e: React.DragEvent, targetStatus: string) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData('text/plain');
    
    // Find the task being dragged
    let taskToMove: KanbanTask | null = null;
    let sourceBoardId = '';
    let sourceColumnId = '';
    
    // Find the task in the current board
    for (const board of boards) {
      for (const column of board.columns) {
        const task = column.tasks.find(t => t.id === taskId);
        if (task) {
          taskToMove = task;
          sourceBoardId = board.id;
          sourceColumnId = column.id;
          break;
        }
      }
      if (taskToMove) break;
    }
    
    if (!taskToMove) return;
    
    // Don't allow dropping on the same column
    if (sourceColumnId === targetStatus) return;
    
    const updatedBoards = boards.map(board => {
      return {
        ...board,
        columns: board.columns.map(column => {
          // Remove task from source column
          if (column.id === sourceColumnId) {
            return {
              ...column,
              tasks: column.tasks.filter(task => task.id !== taskId)
            };
          }
          // Add task to target column
          if (column.id === targetStatus) {
            const updatedTask = {
              ...taskToMove!,
              status: targetStatus as 'todo' | 'in-progress' | 'completed',
              updatedAt: new Date().toISOString(),
              // Set completedAt when moving to completed status
              ...(targetStatus === 'completed' && { completedAt: new Date().toISOString() })
            };
            
            return {
              ...column,
              tasks: [...column.tasks, updatedTask]
            };
          }
          return column;
        })
      };
    });
    
    setBoards(updatedBoards);
    saveData(updatedBoards);
    
    // Clear drag states
    setDraggedTaskId(null);
    setDragOverColumnId(null);
  };

  const addNewBoard = async () => {
    if (!newBoardName.trim()) return;
    
    const newBoard: KanbanBoard = {
      id: `board-${Date.now()}`,
      name: newBoardName,
      description: newBoardDescription,
      columns: [
        {
          id: 'todo',
          title: 'To Do',
          tasks: []
        },
        {
          id: 'in-progress',
          title: 'In Progress',
          tasks: []
        },
        {
          id: 'completed',
          title: 'Completed',
          tasks: []
        }
      ]
    };
    
    const updatedBoards = [...boards, newBoard];
    setBoards(updatedBoards);

    setShowAddBoard(false);
    setNewBoardName('');
    setNewBoardDescription('');
    
    await saveData(updatedBoards);
  };

  const editTask = (task: KanbanTask) => {
    setEditingTask({ ...task });
    setShowEditModal(true);
  };

  const saveTaskEdit = async () => {
    if (!editingTask) return;
    
    const updatedBoards = boards.map(board => ({
      ...board,
      columns: board.columns.map(column => ({
        ...column,
        tasks: column.tasks.map(task => 
          task.id === editingTask.id ? { ...editingTask, updatedAt: new Date().toISOString() } : task
        )
      }))
    }));
    
    setBoards(updatedBoards);
    setShowEditModal(false);
    setEditingTask(null);
    
    await saveData(updatedBoards);
  };

  const deleteTask = (task: KanbanTask) => {
    setTaskToDelete(task);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteTask = async () => {
    if (!taskToDelete) return;
    
    const updatedBoards = boards.map(board => ({
      ...board,
      columns: board.columns.map(column => ({
        ...column,
        tasks: column.tasks.filter(task => task.id !== taskToDelete.id)
      }))
    }));
    
    setBoards(updatedBoards);
    setShowDeleteConfirm(false);
    setTaskToDelete(null);
    
    await saveData(updatedBoards);
  };

  const addComment = async (taskId: string) => {
    const commentText = commentInputs[taskId];
    if (!commentText || !commentText.trim()) return;
    
    const comment: KanbanComment = {
      id: `comment-${Date.now()}`,
      text: commentText,
      author: user?.name || 'Admin',
      timestamp: new Date().toISOString()
    };
    
    const updatedBoards = boards.map(board => ({
      ...board,
      columns: board.columns.map(column => ({
        ...column,
        tasks: column.tasks.map(task => 
          task.id === taskId 
            ? { 
                ...task, 
                comments: [...task.comments, comment],
                updatedAt: new Date().toISOString()
              } 
            : task
        )
      }))
    }));
    
    setBoards(updatedBoards);
    // Clear the comment input for this specific task
    setCommentInputs(prev => ({
      ...prev,
      [taskId]: ''
    }));
    
    await saveData(updatedBoards);
  };

  const getPriorityClass = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'low': return 'priority-low';
      case 'medium': return 'priority-medium';
      case 'high': return 'priority-high';
      case 'critical': return 'priority-critical';
      default: return 'priority-medium';
    }
  };

  const getPageClass = (page: string) => {
    switch (page.toLowerCase()) {
      case 'overview': return 'page-overview';
      case 'characters': return 'page-characters';
      case 'elements': return 'page-elements';
      case 'pillars': return 'page-pillars';
      case 'threads': return 'page-threads';
      case 'kanban': return 'page-kanban';
      case 'admin': return 'page-kanban';
      case 'system': return 'page-overview';
      default: return 'page-overview';
    }
  };

  const formatDate = (date: string) => {
    const d = new Date(date);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Create new task function
  const createNewTask = async () => {
    if (!newTask.title || !newTask.description) {
      alert('Please fill in both title and description');
      return;
    }

    const task: KanbanTask = {
      id: `task-${Date.now()}`,
      title: newTask.title,
      description: newTask.description,
      status: newTask.status as 'todo' | 'in-progress' | 'completed',
      priority: newTask.priority as 'low' | 'medium' | 'high' | 'critical',
      assignee: newTask.assignee || '',
      createdBy: newTask.createdBy || user?.name || 'Admin',
      duration: newTask.duration || '',
      dueDate: newTask.dueDate || '',
      page: newTask.page || 'Overview',
      boardId: 'development',
      comments: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    const updatedBoards = boards.map(board => ({
      ...board,
      columns: board.columns.map(column => ({
        ...column,
        tasks: column.id === task.status 
          ? [...column.tasks, task]
          : column.tasks
      }))
    }));

    setBoards(updatedBoards);
    setShowCreateTask(false);
    
    // Reset new task form
    setNewTask({
      title: '',
      description: '',
      priority: 'medium',
      page: 'Overview',
      assignee: '',
      createdBy: '',
      duration: '',
      dueDate: '',
      status: 'todo'
    });

    await saveData(updatedBoards);
  };

  // Filter functions
  const filterTasks = (tasks: KanbanTask[]) => {
    return tasks.filter(task => {
      const priorityMatch = priorityFilter === 'all' || task.priority === priorityFilter;
      const pageMatch = pageFilter === 'all' || task.page === pageFilter;
      return priorityMatch && pageMatch;
    });
  };

  const clearFilters = () => {
    setPriorityFilter('all');
    setPageFilter('all');
  };

  const currentBoard = boards.length > 0 ? boards[0] : null;



  if (error) {
    return (
      <div className="kanban-container">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  if (!user) {
    return null; // Should not happen if loading and error are handled
  }

  return (
    <div className="kanban-container">
      {/* Loading Overlay */}
      <LoadingOverlay isLoading={loading} message="Loading Kanban data..." />
      
      <div className="kanban-header">
        <h1>Hunt: Kanban Board</h1>
        <div className="header-actions">
          <button 
            className="create-task-btn"
            onClick={() => setShowCreateTask(true)}
            title="Create new task"
          >
            ➕ Create New Task
          </button>
          {saving && <div className="saving-indicator">Saving...</div>}
        </div>
      </div>

      {/* Filter Controls */}
      <div className="filter-controls">
        <div className="filter-controls-container">
          <div className="filter-section">
            <label>Priority Filter:</label>
            <select 
              value={priorityFilter} 
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Priorities</option>
              <option value="low">🟢 Low</option>
              <option value="medium">🟡 Medium</option>
              <option value="high">🔴 High</option>
              <option value="critical">🚨 Critical</option>
            </select>
          </div>
          
          <div className="filter-section">
            <label>Page Filter:</label>
            <select 
              value={pageFilter} 
              onChange={(e) => setPageFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Pages</option>
              <option value="Overview">📊 Overview</option>
              <option value="Characters">👥 Characters</option>
              <option value="Elements">⚡ Elements</option>
              <option value="Pillars">🏛️ Pillars</option>
              <option value="Threads">🧵 Threads</option>
              <option value="Kanban">📋 Kanban</option>
              <option value="Admin">⚙️ Admin</option>
              <option value="System">🔧 System</option>
            </select>
          </div>
          
          <button 
            className="clear-filters-btn"
            onClick={clearFilters}
            title="Clear all filters"
          >
            🗑️ Clear Filters
            </button>    
          
      </div>

         <div className="legend">           
           <div className="legend-items">
             <div className="legend-section">
               <h4>Priority Levels:</h4>
               <div className="legend-badges">
                 <span className="priority-badge priority-low">Low</span>
                 <span className="priority-badge priority-medium">Medium</span>
                 <span className="priority-badge priority-high">High</span>
                 <span className="priority-badge priority-critical">Critical</span>
               </div>
             </div>
             <div className="legend-section">
               <h4>Page Categories:</h4>
               <div className="legend-badges">
                 <span className="page-badge page-overview">Overview</span>
                 <span className="page-badge page-characters">Characters</span>
                 <span className="page-badge page-elements">Elements</span>
                 <span className="page-badge page-pillars">Pillars</span>
                 <span className="page-badge page-threads">Threads</span>
                 <span className="page-badge page-kanban">Kanban</span>
               </div>
             </div>
           </div>
         </div>
       </div>  



      {/* Kanban Board */}
      {currentBoard && (
        <div className="kanban-board">
          {currentBoard.columns.map(column => (
            <div key={column.id} className="kanban-column">
              <h3 className="column-title">{column.title}</h3>
              <div
                className={`column-content ${dragOverColumnId === column.id ? 'drag-over' : ''}`}
                onDragOver={(e) => handleColumnDragOver(e, column.id)}
                onDragLeave={handleColumnDragLeave}
                onDrop={(e) => handleDrop(e, column.id)}
              >
                {filterTasks(column.tasks).map(task => (
                  <div
                    key={task.id}
                    className={`kanban-task ${draggedTaskId === task.id ? 'dragging' : ''}`}
                    draggable
                    onDragStart={(e) => handleDragStart(e, task.id)}
                    onDragEnd={handleDragEnd}
                  >
                    <div className="task-header">
                      <h4>{task.title}</h4>
                      <div className="task-actions">
                        <button 
                          className="action-btn edit-btn"
                          onClick={() => editTask(task)}
                          title="Edit task"
                        >
                          ✏️
                        </button>
                        <button 
                          className="action-btn delete-btn"
                          onClick={() => deleteTask(task)}
                          title="Delete task"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                    
                    <p className="task-description">{task.description}</p>
                    
                                         <div className="task-badges">
                       <span className={`priority-badge ${getPriorityClass(task.priority)}`}>
                         {task.priority}
                       </span>
                       <span className={`page-badge ${getPageClass(task.page)}`}>
                         {task.page}
                       </span>
                     </div>
                     
                     <div className="task-meta-grid">
                       <div className="task-meta-left">
                         <div className="meta-item">
                           <span className="meta-label">🦾 Assignee:</span>
                           <span className="meta-value">{task.assignee || 'Unassigned'}</span>
                         </div>
                         <div className="meta-item">
                           <span className="meta-label">👨‍💻 Created by:</span>
                           <span className="meta-value">{task.createdBy || 'Unknown'}</span>
                         </div>
                         <div className="meta-item">
                           <span className="meta-label"> ⌛ Duration:</span>
                           <span className="meta-value">{task.duration || 'Not set'}</span>
                         </div>
                       </div>
                       <div className="task-meta-right">
                         <div className="meta-item">
                           <span className="meta-label">📅 Created:</span>
                           <span className="meta-value">{formatDate(task.createdAt)}</span>
                         </div>
                         <div className="meta-item">
                           <span className="meta-label">🔄 Updated:</span>
                           <span className="meta-value">{formatDate(task.updatedAt)}</span>
                         </div>
                         <div className="meta-item">
                           <span className="meta-label">✅ Completed:</span>
                           <span className="meta-value">
                             {task.completedAt ? formatDate(task.completedAt) : 'Not completed'}
                           </span>
                         </div>
                       </div>
                     </div>

                    {/* Comments Section */}
                    <div className="task-comments">
                      <h5>Comments ({task.comments.length})</h5>
                      {task.comments.map(comment => (
                        <div key={comment.id} className="comment">
                          <div className="comment-header">
                            <strong>{comment.author}</strong>
                            <small>{formatDate(comment.timestamp)}</small>
                          </div>
                          <p>{comment.text}</p>
                        </div>
                      ))}
                                             <div className="add-comment">
                         <input
                           type="text"
                           placeholder="Add a comment..."
                           value={commentInputs[task.id] || ''}
                           onChange={(e) => setCommentInputs(prev => ({
                             ...prev,
                             [task.id]: e.target.value
                           }))}
                           onKeyPress={(e) => e.key === 'Enter' && addComment(task.id)}
                         />
                         <button onClick={() => addComment(task.id)}>Add</button>
                       </div>
                    </div>
                  </div>
                ))}
                {filterTasks(column.tasks).length === 0 && column.tasks.length > 0 && (
                  <div style={{ 
                    padding: '10px', 
                    textAlign: 'center', 
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontSize: '12px',
                    fontStyle: 'italic'
                  }}>
                    All tasks in this column are filtered out
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Board Modal */}
      {showAddBoard && (
        <div className="modal-overlay" onClick={() => setShowAddBoard(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Add New Board</h3>
            <input
              type="text"
              placeholder="Board name"
              value={newBoardName}
              onChange={(e) => setNewBoardName(e.target.value)}
              className="modal-input"
            />
            <textarea
              placeholder="Board description (optional)"
              value={newBoardDescription}
              onChange={(e) => setNewBoardDescription(e.target.value)}
              className="modal-textarea"
            />
            <div className="modal-actions">
              <button onClick={addNewBoard} className="btn-primary">
                Add Board
              </button>
              <button onClick={() => setShowAddBoard(false)} className="btn-secondary">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

             {/* Edit Task Modal */}
       {showEditModal && editingTask && (
         <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
           <div className="modal edit-task-modal" onClick={(e) => e.stopPropagation()}>
             <div className="modal-header">
               <h3>✏️ Edit Task</h3>
               <button 
                 className="modal-close-btn"
                 onClick={() => setShowEditModal(false)}
                 title="Close"
               >
                 ✕
               </button>
             </div>
             
             <div className="modal-content">
               <div className="form-section">
                 <label>Task Title</label>
                 <input
                   type="text"
                   placeholder="Enter task title..."
                   value={editingTask.title}
                   onChange={(e) => setEditingTask({...editingTask, title: e.target.value})}
                   className="modal-input"
                 />
               </div>

               <div className="form-section">
                 <label>Description</label>
                 <textarea
                   placeholder="Describe the task details..."
                   value={editingTask.description}
                   onChange={(e) => setEditingTask({...editingTask, description: e.target.value})}
                   className="modal-textarea"
                 />
               </div>

               <div className="form-row">
                 <div className="form-section">
                   <label>Priority</label>
                   <select
                     value={editingTask.priority}
                     onChange={(e) => setEditingTask({...editingTask, priority: e.target.value as 'low' | 'medium' | 'high' | 'critical' })}
                     className="modal-select"
                   >
                     <option value="low">🟢 Low</option>
                     <option value="medium">🟡 Medium</option>
                     <option value="high">🔴 High</option>
                     <option value="critical">🚨 Critical</option>
                   </select>
                 </div>

                 <div className="form-section">
                   <label>Page Category</label>
                   <select
                     value={editingTask.page}
                     onChange={(e) => setEditingTask({...editingTask, page: e.target.value})}
                     className="modal-select"
                   >
                     <option value="Overview">📊 Overview</option>
                     <option value="Characters">👥 Characters</option>
                     <option value="Elements">⚡ Elements</option>
                     <option value="Pillars">🏛️ Pillars</option>
                     <option value="Threads">🧵 Threads</option>
                     <option value="Kanban">📋 Kanban</option>
                     <option value="Admin">⚙️ Admin</option>
                     <option value="System">🔧 System</option>
                   </select>
                 </div>
               </div>

               <div className="form-row">
                 <div className="form-section">
                   <label>Assignee</label>
                   <input
                     type="text"
                     placeholder="Who is responsible?"
                     value={editingTask.assignee || ''}
                     onChange={(e) => setEditingTask({...editingTask, assignee: e.target.value})}
                     className="modal-input"
                   />
                 </div>

                 <div className="form-section">
                   <label>Created By</label>
                   <input
                     type="text"
                     placeholder="Who created this task?"
                     value={editingTask.createdBy || ''}
                     onChange={(e) => setEditingTask({...editingTask, createdBy: e.target.value})}
                     className="modal-input"
                   />
                 </div>
               </div>

               <div className="form-row">
                 <div className="form-section">
                   <label>Duration</label>
                   <input
                     type="text"
                     placeholder="e.g., 2 hours, 1 day"
                     value={editingTask.duration || ''}
                     onChange={(e) => setEditingTask({...editingTask, duration: e.target.value})}
                     className="modal-input"
                   />
                 </div>

                 <div className="form-section">
                   <label>Due Date</label>
                   <input
                     type="date"
                     value={editingTask.dueDate || ''}
                     onChange={(e) => setEditingTask({...editingTask, dueDate: e.target.value})}
                     className="modal-input"
                   />
                 </div>
               </div>

               <div className="form-section">
                 <label>Current Status</label>
                 <div className="status-display">
                   <span className={`status-badge status-${editingTask.status}`}>
                     {editingTask.status === 'todo' && '📝 To Do'}
                     {editingTask.status === 'in-progress' && '🔄 In Progress'}
                     {editingTask.status === 'completed' && '✅ Completed'}
                   </span>
                   <small>Status can be changed by dragging the task between columns</small>
                 </div>
               </div>
             </div>

             <div className="modal-actions">
               <button onClick={() => setShowEditModal(false)} className="btn-secondary">
                 Cancel
               </button>
               <button onClick={saveTaskEdit} className="btn-primary">
                 💾 Save Changes
               </button>
             </div>
           </div>
         </div>
       )}

      {/* Create New Task Modal */}
      {showCreateTask && (
        <div className="modal-overlay" onClick={() => setShowCreateTask(false)}>
          <div className="modal edit-task-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>➕ Create New Task</h3>
              <button 
                className="modal-close-btn"
                onClick={() => setShowCreateTask(false)}
                title="Close"
              >
                ✕
              </button>
            </div>
            
            <div className="modal-content">
              <div className="form-section">
                <label>Task Title *</label>
                <input
                  type="text"
                  placeholder="Enter task title..."
                  value={newTask.title}
                  onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                  className="modal-input"
                />
              </div>

              <div className="form-section">
                <label>Description *</label>
                <textarea
                  placeholder="Describe the task details..."
                  value={newTask.description}
                  onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                  className="modal-textarea"
                />
              </div>

              <div className="form-row">
                <div className="form-section">
                  <label>Priority</label>
                  <select
                    value={newTask.priority}
                    onChange={(e) => setNewTask({...newTask, priority: e.target.value as 'low' | 'medium' | 'high' | 'critical' })}
                    className="modal-select"
                  >
                    <option value="low">🟢 Low</option>
                    <option value="medium">🟡 Medium</option>
                    <option value="high">🔴 High</option>
                    <option value="critical">🚨 Critical</option>
                  </select>
                </div>

                <div className="form-section">
                  <label>Page Category</label>
                  <select
                    value={newTask.page}
                    onChange={(e) => setNewTask({...newTask, page: e.target.value})}
                    className="modal-select"
                  >
                    <option value="Overview">📊 Overview</option>
                    <option value="Characters">👥 Characters</option>
                    <option value="Elements">⚡ Elements</option>
                    <option value="Pillars">🏛️ Pillars</option>
                    <option value="Threads">🧵 Threads</option>
                    <option value="Kanban">📋 Kanban</option>
                    <option value="Admin">⚙️ Admin</option>
                    <option value="System">🔧 System</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-section">
                  <label>Assignee</label>
                  <input
                    type="text"
                    placeholder="Who is responsible?"
                    value={newTask.assignee || ''}
                    onChange={(e) => setNewTask({...newTask, assignee: e.target.value})}
                    className="modal-input"
                  />
                </div>

                <div className="form-section">
                  <label>Created By</label>
                  <input
                    type="text"
                    placeholder="Who created this task?"
                    value={newTask.createdBy || ''}
                    onChange={(e) => setNewTask({...newTask, createdBy: e.target.value})}
                    className="modal-input"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-section">
                  <label>Duration</label>
                  <input
                    type="text"
                    placeholder="e.g., 2 hours, 1 day"
                    value={newTask.duration || ''}
                    onChange={(e) => setNewTask({...newTask, duration: e.target.value})}
                    className="modal-input"
                  />
                </div>

                <div className="form-section">
                  <label>Due Date</label>
                  <input
                    type="date"
                    value={newTask.dueDate || ''}
                    onChange={(e) => setNewTask({...newTask, dueDate: e.target.value})}
                    className="modal-input"
                  />
                </div>
              </div>

              <div className="form-section">
                <label>Initial Status</label>
                <select
                  value={newTask.status}
                  onChange={(e) => setNewTask({...newTask, status: e.target.value as 'todo' | 'in-progress' | 'completed'})}
                  className="modal-select"
                >
                  <option value="todo">📝 To Do</option>
                  <option value="in-progress">🔄 In Progress</option>
                  <option value="completed">✅ Completed</option>
                </select>
              </div>
            </div>

            <div className="modal-actions">
              <button onClick={() => setShowCreateTask(false)} className="btn-secondary">
                Cancel
              </button>
              <button onClick={createNewTask} className="btn-primary">
                ➕ Create Task
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && taskToDelete && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Task</h3>
            <p>Are you sure you want to delete "{taskToDelete.title}"?</p>
            <p>This action cannot be undone.</p>
            <div className="modal-actions">
              <button onClick={confirmDeleteTask} className="btn-danger">
                Delete
              </button>
              <button onClick={() => setShowDeleteConfirm(false)} className="btn-secondary">
                Cancel
              </button>
            </div>
          </div>
        </div>
             )}

       
     </div>
   );
 };

export default Kanban; 